import { createBrowserRouter, RouterProvider, Outlet, useLocation, Navigate } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { useEffect, lazy, Suspense } from 'react'
import { Analytics } from '@vercel/analytics/react'
import Header from './components/Header'
import Footer from './components/Footer'

// Import Home directly for faster LCP on mobile
import Home from './pages/Home'

// Lazy load other pages for better performance
const Demo = lazy(() => import('./pages/Demo'))
const Contact = lazy(() => import('./pages/Contact'))
const Next = lazy(() => import('./pages/Next'))
const Privacy = lazy(() => import('./pages/Privacy'))
const Terms = lazy(() => import('./pages/Terms'))
const Partners = lazy(() => import('./pages/Partners'))
const Chat = lazy(() => import('./pages/Chat'))
const Pricing = lazy(() => import('./pages/Pricing'))
const FAQ = lazy(() => import('./pages/FAQ'))
const CaseStudyVideo = lazy(() => import('./pages/CaseStudyVideo'))
const Booked = lazy(() => import('./pages/Booked'))

// Loading component for suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-pulse text-primary-600">
      <svg className="w-8 h-8 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    </div>
  </div>
)

function CaseStudy2RegisterRedirect() {
  const location = useLocation()

  useEffect(() => {
    window.location.replace(`/case-study-1-register${location.search}${location.hash}`)
  }, [location.search, location.hash])

  return <PageLoader />
}

function CaseStudyShortlinkRedirect() {
  const location = useLocation()

  return <Navigate to={`/case-study-1-video${location.search}${location.hash}`} replace />
}

function Layout() {
  const location = useLocation()
  
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])
  
  // Hide header on /demo, /next, and /chat pages
  const hideHeader = 
    location.pathname === '/demo' || 
    location.pathname === '/next' || 
    location.pathname === '/chat' ||
    location.pathname === '/booked' ||
    location.pathname === '/casestudy' ||
    location.pathname.startsWith('/case-study-1') ||
    location.pathname.startsWith('/case-study-2-register')
  
  // Don't use Suspense for home page to ensure fast LCP
  const isHomePage = location.pathname === '/'
  
  return (
    <div className="min-h-screen flex flex-col">
      {!hideHeader && <Header />}
      <main className="flex-grow">
        {isHomePage ? (
          <Outlet />
        ) : (
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        )}
      </main>
      <Footer />
    </div>
  )
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    children: [
      {
        index: true,
        element: <Home />
      },
      {
        path: "demo",
        element: <Demo />
      },
      {
        path: "contact",
        element: <Contact />
      },
      {
        path: "next",
        element: <Next />
      },
      {
        path: "privacy",
        element: <Privacy />
      },
      {
        path: "terms",
        element: <Terms />
      },
      {
        path: "partners",
        element: <Partners />
      },
      {
        path: "chat",
        element: <Chat />
      },
      {
        path: "pricing",
        element: <Pricing />
      },
      {
        path: "faq",
        element: <FAQ />
      },
      {
        path: "casestudy",
        element: <CaseStudyShortlinkRedirect />
      },
      {
        path: "case-study-2-register",
        element: <CaseStudy2RegisterRedirect />
      },
      {
        path: "case-study-1-video",
        element: <CaseStudyVideo />
      },
      {
        path: "booked",
        element: <Booked />
      }
    ]
  }
], {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true,
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true
  }
})

function App() {
  return (
    <HelmetProvider>
      <>
        <RouterProvider router={router} />
        <Analytics />
      </>
    </HelmetProvider>
  )
}

export default App
